import { useCallback, useEffect, useMemo, useState } from "react";
import {
  isWithinWindow,
  resolveCostPerMl,
  specTotals,
  UNITS_FOR_KIND,
  wastageCost,
  weekBoundsIn,
  windowLabel,
  type DateWindow,
  type Ingredient,
  type Spec,
  type WastageKind,
  type WastageSubject,
  type WastageUnit, clockOf, type Zone } from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { money } from "../format.js";
import { NoVenue, useActingVenue } from "../NoVenue.js";
import type { ScreenProps } from "../screens.js";
import {
  wastageApi,
  wastageReasons,
  type WastageEntry,
} from "./api.js";
import "./wastage.css";

const UNIT_LABEL: Readonly<Record<WastageUnit, string>> = {
  ml: "mL",
  serve: "serves",
  bottle: "bottles",
};

const KEYS = ["7", "8", "9", "4", "5", "6", "1", "2", "3", ".", "0", "⌫"];

/**
 * The trading week an entry belongs to, decided on the venue's clock.
 *
 * This used to run off `getFullYear`/`getDay` — the *tablet's* zone, which is
 * the venue's only while the tablet stays in the venue — and a Sunday 11 pm
 * entry logged anywhere else, or read back by the dashboard in UTC, fell into
 * the next week. `weekBoundsIn` applies the same cut-off rollover the service
 * date does, so the last round of a Sunday night counts in the week it was
 * poured in. See issue #60.
 */
const weekOf = (at: Date, tz: Zone): DateWindow => {
  const { start, end } = weekBoundsIn(at, tz);
  return { from: start, to: end };
};

const when = (iso: string, timeZone: string) =>
  new Date(iso).toLocaleString("en-AU", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

/** What the picker offers: every ingredient, and every spec, priced. */
interface Choice {
  readonly id: string;
  readonly kind: WastageKind;
  readonly name: string;
  /** `$0.069 / mL` or `$4.21 / serve`. */
  readonly rate: string;
  readonly subject: WastageSubject;
}

/**
 * Screen 08. Log wastage in three taps — what went, why, how much — and read
 * back the week with a manager's approval control.
 *
 * The live cost uses the same `@baros/core` function the API uses, against the
 * same resolved prices, so the figure on the tile is the figure that gets
 * stored. See routes/wastage.ts.
 */
export function WastageScreen(_props: ScreenProps) {
  const app = useApp();
  const [tab, setTab] = useState<"log" | "week">("log");
  const [kind, setKind] = useState<WastageKind>("ingredient");
  const [query, setQuery] = useState("");
  const [itemId, setItemId] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState<WastageUnit>("ml");
  const [note, setNote] = useState("");

  const [entries, setEntries] = useState<readonly WastageEntry[] | null>(null);
  const [weekIndex, setWeekIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const reasons = useMemo(() => wastageReasons(app.me), [app.me]);
  const canApprove = app.me.can["wastage:approve"];
  const venueId = useActingVenue();
  /** The venue's zone, off its META — every week here is bounded in it. */
  // The venue's clock: its weeks start on its own day and end at its cut-off.
  const clock = clockOf(app.venueState?.venue);
  const tz = clock.timeZone;

  // Venue price overrides, where the venue buys at a different price than the
  // org default (§5). The shared VenueState type does not declare `stock` yet,
  // so it is narrowed here rather than in the shared client.
  const costOverrides = useMemo(() => {
    const stock = (app.venueState as { stock?: readonly Record<string, unknown>[] } | null)?.stock;
    return new Map(
      (stock ?? []).map((s) => [
        String(s["ingredientId"]),
        s["costPerMl"] as number | null | undefined,
      ]),
    );
  }, [app.venueState]);

  const ingredients = useMemo(() => {
    const priced = app.library.ingredients.map((i) => ({
      ...i,
      costPerMl: resolveCostPerMl(i.costPerMl, costOverrides.get(i.ingredientId)),
    }));
    return new Map<string, Ingredient>(priced.map((i) => [i.ingredientId, i]));
  }, [app.library.ingredients, costOverrides]);

  const staffNames = useMemo(
    () => new Map((app.venueState?.staff ?? []).map((s) => [s.userId, s.displayName])),
    [app.venueState],
  );

  const choices = useMemo<readonly Choice[]>(() => {
    if (kind === "ingredient") {
      return [...ingredients.values()]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((i) => ({
          id: i.ingredientId,
          kind: "ingredient" as const,
          name: i.name,
          rate: `$${i.costPerMl.toFixed(3)} / mL`,
          subject: {
            kind: "ingredient" as const,
            ingredient: { costPerMl: i.costPerMl, bottleMl: i.bottleMl },
          },
        }));
    }
    return [...app.library.specs]
      .filter((s: Spec) => s.status === "available")
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((s) => {
        const totals = specTotals(s, ingredients);
        return {
          id: s.specId,
          kind: "spec" as const,
          name: s.name,
          rate: `${money(totals.cost)} / serve`,
          subject: { kind: "spec" as const, totals },
        };
      });
  }, [kind, ingredients, app.library.specs]);

  const nameOf = useMemo(() => {
    const names = new Map<string, string>();
    for (const i of app.library.ingredients) names.set(`ingredient:${i.ingredientId}`, i.name);
    for (const s of app.library.specs) names.set(`spec:${s.specId}`, s.name);
    return names;
  }, [app.library]);

  const selected = choices.find((c) => c.id === itemId) ?? null;
  const qtyNumber = qty === "" ? 0 : Number.parseFloat(qty);
  const live = selected
    ? wastageCost(selected.subject, qtyNumber, unit)
    : { ml: 0, costDollars: 0 };
  const ready = Boolean(selected && reason && qtyNumber > 0 && !busy && venueId);

  const load = useCallback(async () => {
    if (!venueId) return;
    try {
      const res = await wastageApi.list(app.token, venueId);
      setEntries(res.wastage);
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not load wastage" });
      setEntries([]);
    }
  }, [app.token, venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Weeks that have something in them, newest first, with this week at the
  // front whether or not anything has been logged in it yet.
  const weeks = useMemo(() => {
    const found = new Map<string, DateWindow>();
    const now = weekOf(new Date(), clock);
    found.set(now.from, now);
    for (const e of entries ?? []) {
      const week = weekOf(new Date(e.at), clock);
      found.set(week.from, week);
    }
    return [...found.values()].sort((a, b) => b.from.localeCompare(a.from));
  }, [entries, tz]);

  // Open on the most recent week that actually has entries — a demo or a
  // Monday morning should not land on an empty list.
  useEffect(() => {
    if (!entries || entries.length === 0) return;
    const newest = weekOf(new Date(entries[0]!.at), clock);
    const idx = weeks.findIndex((w) => w.from === newest.from);
    if (idx > 0) setWeekIndex(idx);
  }, [entries, weeks, tz]);

  const shownWeek = weeks[Math.min(weekIndex, weeks.length - 1)] ?? weekOf(new Date(), clock);
  // The same predicate the dashboard's week tile applies to the same rows, so
  // the two figures cannot disagree.
  const weekRows = useMemo(
    () => (entries ?? []).filter((e) => isWithinWindow(e.at, shownWeek, clock)),
    [entries, shownWeek, tz],
  );

  const weekTotal = weekRows.reduce((t, e) => t + e.costDollars, 0);
  const awaiting = weekRows.filter((e) => !e.approvedBy).length;

  const byReason = useMemo(() => {
    const tally = new Map<string, number>();
    for (const e of weekRows) tally.set(e.reason, (tally.get(e.reason) ?? 0) + e.costDollars);
    return [...tally.entries()].sort((a, b) => b[1] - a[1]);
  }, [weekRows]);

  function pick(choice: Choice) {
    setItemId(choice.id);
    const allowed = UNITS_FOR_KIND[choice.kind];
    if (!allowed.includes(unit)) setUnit(allowed[0] ?? "ml");
  }

  function switchKind(next: WastageKind) {
    setKind(next);
    setItemId(null);
    setQuery("");
    setUnit(UNITS_FOR_KIND[next][0] ?? "ml");
  }

  function press(key: string) {
    setQty((current) => {
      if (key === "⌫") return current.slice(0, -1);
      if (key === "." && current.includes(".")) return current;
      if (current.replace(".", "").length >= 6) return current;
      return current + key;
    });
  }

  function reset() {
    setItemId(null);
    setReason(null);
    setQty("");
    setNote("");
    setMsg(null);
  }

  async function log() {
    if (!selected || !reason || !venueId) return;
    setBusy(true);
    setMsg(null);
    try {
      const { wastage } = await wastageApi.log(app.token, venueId, {
        reason,
        kind: selected.kind,
        ...(selected.kind === "ingredient" ? { ingredientId: selected.id } : { specId: selected.id }),
        qty: qtyNumber,
        unit,
        note,
      });
      setEntries((current) => [wastage, ...(current ?? [])]);
      setMsg({ kind: "ok", text: `${selected.name} · ${money(wastage.costDollars)} logged` });
      setItemId(null);
      setReason(null);
      setQty("");
      setNote("");
    } catch (err) {
      setMsg({
        kind: "err",
        text: err instanceof ApiError ? err.message : "Could not log that wastage",
      });
    } finally {
      setBusy(false);
    }
  }

  async function approve(entry: WastageEntry) {
    if (!venueId) return;
    try {
      const { wastage } = await wastageApi.approve(app.token, venueId, entry.wasteId);
      setEntries((current) => (current ?? []).map((e) => (e.wasteId === entry.wasteId ? wastage : e)));
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not approve" });
    }
  }

  async function remove(entry: WastageEntry) {
    if (!venueId) return;
    try {
      await wastageApi.remove(app.token, venueId, entry.wasteId);
      setEntries((current) => (current ?? []).filter((e) => e.wasteId !== entry.wasteId));
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not delete" });
    }
  }

  if (!venueId) return <NoVenue what="a wastage log" />;

  const q = query.trim().toLowerCase();
  const filtered = q ? choices.filter((c) => c.name.toLowerCase().includes(q)) : choices;
  const units = selected ? UNITS_FOR_KIND[selected.kind] : UNITS_FOR_KIND[kind];

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            {tab === "log" ? "1 · What went" : `Week of ${windowLabel(shownWeek)}`}
          </div>
        </div>
        <span className="spacer" />
        <button className="btn" aria-pressed={tab === "log"} onClick={() => setTab("log")}>
          Log
        </button>
        <button className="btn" aria-pressed={tab === "week"} onClick={() => setTab("week")}>
          The week
          <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{weekRows.length}</span>
        </button>
      </div>

      {msg && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className={`msg ${msg.kind === "err" ? "err" : "ok"}`}>{msg.text}</div>
        </div>
      )}

      {tab === "log" ? (
        <div className="split w-log">
          <section className="pane" style={{ padding: "0 20px 20px", gap: 16 }}>
            <div className="row" style={{ paddingBottom: 12 }}>
              <button
                className="btn"
                aria-pressed={kind === "ingredient"}
                onClick={() => switchKind("ingredient")}
              >
                A bottle
              </button>
              <button className="btn" aria-pressed={kind === "spec"} onClick={() => switchKind("spec")}>
                A whole cocktail
              </button>
              <input
                type="text"
                placeholder={kind === "ingredient" ? "Search ingredients" : "Search drinks"}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-search"
              />
            </div>

            <div className="scroll" style={{ marginBottom: 14 }}>
              <div className="w-tiles">
                {filtered.map((c) => (
                  <button
                    key={c.id}
                    className="w-tile"
                    aria-pressed={itemId === c.id}
                    onClick={() => pick(c)}
                  >
                    <span className="n">{c.name}</span>
                    <span className="r">{c.rate}</span>
                  </button>
                ))}
              </div>
              {filtered.length === 0 && <div className="label" style={{ padding: "12px 0" }}>Nothing matches.</div>}
            </div>

            <div className="w-step">2 · Why</div>
            <div className="w-reasons">
              {reasons.map((r) => (
                <button
                  key={r}
                  className="btn"
                  aria-pressed={reason === r}
                  onClick={() => setReason(reason === r ? null : r)}
                >
                  {r}
                </button>
              ))}
            </div>

            <label className="field">
              <span>Note — optional</span>
              <input
                type="text"
                placeholder="What happened"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>

            <div className="w-entry">
              <div>
                <div className="label">This entry</div>
                <div className="v">{money(live.costDollars)}</div>
              </div>
              <div className="summary">
                {selected ? selected.name : "Nothing picked"}
                <br />
                {reason ?? "No reason"} · {qty || "0"} {UNIT_LABEL[unit]}
                {selected && live.ml > 0 ? ` · ${live.ml.toFixed(0)} mL` : ""}
              </div>
              <span className="spacer" />
              <button className="btn primary" onClick={reset}>Clear</button>
              <button className="btn primary" disabled={!ready} onClick={() => void log()}>
                {busy ? "Logging…" : "Log wastage"}
              </button>
            </div>
          </section>

          <section className="aside w-aside">
            <div className="row" style={{ alignItems: "baseline" }}>
              <div>
                <div className="w-step">3 · How much</div>
                <div className="label" style={{ marginTop: 4 }}>
                  {selected ? selected.rate : "Pick something first"}
                </div>
              </div>
              <span className="spacer" />
              {units.map((u) => (
                <button key={u} className="btn" aria-pressed={unit === u} onClick={() => setUnit(u)}>
                  {UNIT_LABEL[u]}
                </button>
              ))}
            </div>

            <div className="w-qty">
              <span className="v">{qty || "0"}</span>
              <span className="u">{UNIT_LABEL[unit]}</span>
            </div>

            <div className="w-keypad">
              {KEYS.map((k) => (
                <button key={k} onClick={() => press(k)} aria-label={k === "⌫" ? "Backspace" : k}>
                  {k}
                </button>
              ))}
            </div>
          </section>
        </div>
      ) : (
        <>
          <div className="row" style={{ padding: "0 20px 12px" }}>
            <button
              className="btn"
              disabled={weekIndex >= weeks.length - 1}
              onClick={() => setWeekIndex((i) => Math.min(weeks.length - 1, i + 1))}
            >
              ← Earlier
            </button>
            <button className="btn" disabled={weekIndex <= 0} onClick={() => setWeekIndex((i) => Math.max(0, i - 1))}>
              Later →
            </button>
            <span className="spacer" />
            <div className="w-reason-tally">
              {byReason.map(([r, cost]) => (
                <span key={r} className="chip">
                  <span className="label" style={{ letterSpacing: "0.1em" }}>{r}</span>
                  <span className="num">{money(cost)}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            <div className="table">
              <div className="thead">
                <span className="w-c-when">When</span>
                <span className="w-c-reason">Reason</span>
                <span className="c-name">Item</span>
                <span className="w-c-qty">Quantity</span>
                <span className="w-c-cost" style={{ textAlign: "right" }}>Cost</span>
                <span className="w-c-who">Logged by</span>
                <span className="w-c-act">Approval</span>
              </div>
              {weekRows.map((e) => {
                const key = `${e.kind}:${e.kind === "spec" ? e.specId : e.ingredientId}`;
                return (
                  <div key={e.wasteId} className="trow">
                    <span className="w-c-when">{when(e.at, tz)}</span>
                    <span className="w-c-reason">{e.reason}</span>
                    <span className="c-name">
                      <span className="n">{nameOf.get(key) ?? key}</span>
                      <span className="s">
                        {e.kind === "spec" ? "Finished drink" : "Ingredient"}
                        {e.note ? <span>· {e.note}</span> : null}
                      </span>
                    </span>
                    <span className="w-c-qty">
                      {e.qty} {UNIT_LABEL[e.unit]}
                    </span>
                    <span className="w-c-cost">{money(e.costDollars)}</span>
                    <span className="w-c-who">{staffNames.get(e.byUserId) ?? "—"}</span>
                    <span className="w-c-act">
                      {e.approvedBy ? (
                        <span className="label pos" style={{ letterSpacing: "0.1em" }}>
                          Approved · {staffNames.get(e.approvedBy) ?? "manager"}
                        </span>
                      ) : canApprove ? (
                        <>
                          <button className="btn sm" onClick={() => void approve(e)}>Approve</button>
                          <button className="btn sm danger" onClick={() => void remove(e)}>Delete</button>
                        </>
                      ) : (
                        <span className="label warn" style={{ letterSpacing: "0.1em" }}>Awaiting sign-off</span>
                      )}
                    </span>
                  </div>
                );
              })}
              {entries === null && <div className="pad label">Loading the week…</div>}
              {entries !== null && weekRows.length === 0 && (
                <div className="pad label">Nothing logged this week.</div>
              )}
            </div>
          </div>

          <div className="w-foot">
            <div>
              <div className="label">{weekRows.length} entries · cost of the week</div>
              <div className="v">{money(weekTotal)}</div>
            </div>
            <div>
              <div className="label">Awaiting sign-off</div>
              <div className="v" style={{ color: awaiting ? "var(--warn)" : "var(--pos)" }}>
                {awaiting}
              </div>
            </div>
            <span className="spacer" />
            <button className="btn primary" onClick={() => setTab("log")}>Log wastage</button>
          </div>
        </>
      )}
    </div>
  );
}
